// Page-owned SuperPlumma control for planning surfaces. These adapters only
// coordinate existing React state and backend callbacks; scenario calculations,
// goal tracking and budget persistence remain in their canonical owners.
const advisorControlText = (value: unknown) => String(value || '').trim()
const advisorControlNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}
const advisorControlId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
const advisorControlExact = (rows: any[], id: unknown) => {
  const key = advisorControlText(id)
  const row = rows.find((entry) => advisorControlText(entry?.id ?? entry?.code) === key)
  if (!key || !row) throw new Error('pageControl.recordNotFound')
  return row
}

const createAiAdvisorPageControlAdapter = (c: any) => ({
  read: () => ({
    tab: c.tab,
    horizonMonths: c.horizon,
    forecast: c.forecast,
    scenarios: c.scenarios.map((scenario: any) => {
      const inputs = c.scenarioInputs[scenario.id] || {}
      return {
        id: scenario.id,
        active: c.scenarioIds.includes(scenario.id),
        editable: scenario.editable !== false,
        repeatable: Boolean(scenario.repeatable),
        pct: scenario.slider ? c.pctOf(scenario, inputs) : undefined,
        slider: scenario.slider || undefined,
        rows: scenario.repeatable ? c.rowsOf(scenario, inputs) : undefined,
        rowFields: scenario.rowFields || undefined,
        loanFields: scenario.loanFields || undefined,
      }
    }),
    goals: c.goals.map(({ id, title, metric, target, deadline, current, progress, reached, overdue, onTrack }: any) => (
      { id, title, metric, target, deadline, current, progress, reached, overdue, onTrack }
    )),
    plannedEvents: c.events.map(({ id, label, kind, date, amount }: any) => ({ id, label, kind, date, amount })),
  }),
  perform: async (step: any) => {
    const fields = step.fields || {}
    if (step.action === 'set_fields') {
      if (fields.tab !== undefined) {
        if (!['oversikt', 'likviditet', 'salg', 'kostnader', 'budsjett', 'skatt', 'innsikt'].includes(fields.tab)) throw new Error('pageControl.unsupported')
        c.setTab(fields.tab)
      }
      if (step.operation === 'set_forecast_horizon') {
        if (![3, 6, 12].includes(Number(fields.horizonMonths))) throw new Error('pageControl.incomplete')
        c.setHorizon(Number(fields.horizonMonths))
        c.setTab('likviditet')
        return { result: 'forecast_horizon_updated', horizonMonths: Number(fields.horizonMonths) }
      }
      if (step.operation === 'clear_scenarios') {
        c.clearScenarios()
        c.setTab('likviditet')
        return { result: 'scenarios_cleared' }
      }
      if (step.operation === 'set_scenario') {
        const scenario = advisorControlExact(c.scenarios, fields.scenarioId)
        if (scenario.editable === false && (fields.scenarioRows || fields.scenarioPct !== undefined)) throw new Error('pageControl.unsupported')
        c.setScenario({
          id: scenario.id,
          enabled: fields.scenarioEnabled !== false,
          rows: fields.scenarioRows,
          pct: fields.scenarioPct,
        })
        c.setTab('likviditet')
        return { result: fields.scenarioEnabled === false ? 'scenario_disabled' : 'scenario_updated', scenarioId: scenario.id }
      }
      return { result: 'fields_applied' }
    }
    if (step.action === 'submit' && step.operation === 'save_goal') {
      const existing = fields.goalId ? advisorControlExact(c.goals, fields.goalId) : null
      const next = {
        ...(existing || {}),
        id: existing?.id || advisorControlId('goal'),
        title: fields.goalTitle !== undefined ? advisorControlText(fields.goalTitle) : existing?.title,
        metric: fields.goalMetric !== undefined ? fields.goalMetric : existing?.metric,
        target: fields.goalTarget !== undefined ? advisorControlNumber(fields.goalTarget) : existing?.target,
        deadline: fields.goalDeadline !== undefined ? fields.goalDeadline : existing?.deadline,
      }
      if (!next.title || !['buffer', 'income', 'result', 'margin'].includes(next.metric) || !(next.target > 0) || !next.deadline) throw new Error('pageControl.incomplete')
      const saved = await c.saveGoal(next)
      c.setTab('budsjett')
      return { result: existing ? 'goal_updated' : 'goal_created', recordId: saved?.id || next.id }
    }
    if (step.action === 'delete' && step.operation === 'delete_goal') {
      const goal = advisorControlExact(c.goals, fields.goalId)
      await c.deleteGoal(goal.id)
      c.setTab('budsjett')
      return { result: 'goal_deleted', recordId: goal.id }
    }
    if (step.action === 'submit' && step.operation === 'save_planned_event') {
      const existing = fields.eventId ? advisorControlExact(c.events, fields.eventId) : null
      const absolute = fields.eventAmount !== undefined ? Math.abs(advisorControlNumber(fields.eventAmount)) : Math.abs(advisorControlNumber(existing?.amount))
      const direction = fields.eventDirection || (advisorControlNumber(existing?.amount) > 0 ? 'in' : 'out')
      const next = {
        ...(existing || {}),
        id: existing?.id || advisorControlId('event'),
        label: fields.eventLabel !== undefined ? advisorControlText(fields.eventLabel) : existing?.label,
        kind: fields.eventKind !== undefined ? fields.eventKind : existing?.kind,
        date: fields.eventDate !== undefined ? fields.eventDate : existing?.date,
        amount: direction === 'out' ? -absolute : absolute,
      }
      if (!next.label || !['salary', 'tax', 'supplier', 'investment', 'loan', 'other'].includes(next.kind) || !next.date || !(absolute > 0)) throw new Error('pageControl.incomplete')
      const saved = await c.saveEvent(next)
      c.setTab('budsjett')
      return { result: existing ? 'planned_event_updated' : 'planned_event_created', recordId: saved?.id || next.id }
    }
    if (step.action === 'delete' && step.operation === 'delete_planned_event') {
      const event = advisorControlExact(c.events, fields.eventId)
      await c.deleteEvent(event.id)
      c.setTab('budsjett')
      return { result: 'planned_event_deleted', recordId: event.id }
    }
    throw new Error('pageControl.unsupported')
  },
})

const createBudgetPageControlAdapter = (c: any) => {
  const normalizeLines = (lines: any[]) => lines.map((line: any) => {
    const account = advisorControlExact(c.accountOptions, line.accountCode)
    const code = advisorControlText(account.code)
    const inferredKind = account.category === 'income' || code.startsWith('3') ? 'income' : 'cost'
    if (line.kind !== inferredKind) throw new Error('pageControl.unsupported')
    const monthly = Array.isArray(line.monthlyAmounts) ? line.monthlyAmounts.map((value: unknown) => Math.max(0, advisorControlNumber(value))) : null
    const annual = monthly ? monthly.reduce((sum: number, value: number) => sum + value, 0) : Math.max(0, advisorControlNumber(line.annualAmount))
    if (!(annual > 0)) throw new Error('pageControl.incomplete')
    return {
      id: advisorControlText(line.id) || advisorControlId(`budget-${code}`),
      accountCode: code,
      accountName: advisorControlText(account.name),
      kind: inferredKind,
      annualAmount: annual,
      monthlyAmounts: monthly || c.splitYear(annual),
    }
  })
  return {
    read: () => ({
      year: c.year,
      view: c.view,
      unsaved: Boolean(c.unsaved),
      lines: c.lines,
      accountOptions: c.accountOptions.map(({ code, name, category }: any) => ({ code, name, category })),
    }),
    perform: async (step: any) => {
      const fields = step.fields || {}
      if (step.action === 'set_fields') {
        if (fields.view !== undefined) {
          if (!['oversikt', 'analyse', 'budsjett'].includes(fields.view)) throw new Error('pageControl.unsupported')
          c.setView(fields.view)
        }
        if (step.operation === 'set_budget_lines') {
          if (!Array.isArray(fields.budgetLines) || !fields.budgetLines.length) throw new Error('pageControl.incomplete')
          const incoming = normalizeLines(fields.budgetLines)
          const next = fields.budgetMode === 'replace'
            ? incoming
            : [...c.lines.filter((line: any) => !incoming.some((item: any) => item.accountCode === line.accountCode && item.kind === line.kind)), ...incoming]
          c.applyLines(next)
          c.setView('budsjett')
          return { result: 'budget_draft_updated', lineCount: next.length }
        }
        return { result: 'fields_applied' }
      }
      if (step.action === 'submit' && step.operation === 'save_budget') {
        if (!c.lines.length) throw new Error('pageControl.incomplete')
        await c.save(c.lines)
        c.setView('budsjett')
        return { result: 'budget_saved', lineCount: c.lines.length, year: c.year }
      }
      throw new Error('pageControl.unsupported')
    },
  }
}

function useAiAdvisorPageControlAdapter(c: any) {
  const backend = (globalThis as any).PlummeBackend
  const plan = backend?.getAiAdvisorPlanModel?.({ year: c.year }) || { goals: [], events: [] }
  const scenarios = c.scenarioModel.scenarios || []
  const setScenario = ({ id, enabled, rows, pct }: any) => {
    c.setSimulated(null)
    c.setScenarioIds((current: string[]) => enabled ? (current.includes(id) ? current : [...current, id]) : current.filter((entry) => entry !== id))
    if (!enabled || (!Array.isArray(rows) && pct === undefined)) return
    const scenario = scenarios.find((entry: any) => entry.id === id)
    c.setScenarioInputs((current: any) => {
      const next = { ...(current[id] || {}) }
      if (Array.isArray(rows)) next.rows = rows.map((row: any) => ({ ...(c.scenarioApi?.defaultRow?.(scenario) || {}), ...row }))
      if (pct !== undefined) next.pct = pct
      return { ...current, [id]: next }
    })
  }
  usePageOwnedControlAdapter('ai_advisor', createAiAdvisorPageControlAdapter({
    tab: c.tab, setTab: c.setTab, horizon: c.horizon, setHorizon: c.setHorizon,
    forecast: { year: c.year, displayedFrom: c.cfData[0]?.m || null, displayedTo: c.cfData[c.cfData.length - 1]?.m || null,
      baseEndBalance: c.endBalance, scenarioEndBalance: c.scenarioAfter?.[c.scenarioAfter.length - 1]?.balance ?? null, buffer: c.buffer },
    scenarios, scenarioIds: c.scenarioIds, scenarioInputs: c.scenarioInputs,
    rowsOf: (scenario: any, inputs: any) => c.scenarioApi?.rowsOf?.(scenario, inputs) || [],
    pctOf: (scenario: any, inputs: any) => c.scenarioApi?.pctOf?.(scenario, inputs) || 0,
    setScenario, clearScenarios: () => { c.setScenarioIds([]); c.setSimulated(null) },
    goals: plan.goals || [], events: plan.events || [],
    saveGoal: (goal: any) => backend.saveAdvisorGoal(goal), deleteGoal: (id: string) => backend.deleteAdvisorGoal(id),
    saveEvent: (event: any) => backend.savePlannedCashEvent(event), deleteEvent: (id: string) => backend.deletePlannedCashEvent(id),
  }))
}

function useBudgetPageControlAdapter(c: any) {
  const lines = c.accounts.map(c.accountToBudgetLine)
  const stored = (c.model?.budgetLines || []).map((line: any) => c.accountToBudgetLine(c.budgetLineToAccount(line)))
  usePageOwnedControlAdapter('budget', createBudgetPageControlAdapter({
    year: c.activeYear, view: c.view, setView: c.setView, lines, accountOptions: c.accountOptions, splitYear: c.splitYear,
    unsaved: JSON.stringify(lines) !== JSON.stringify(stored), applyLines: (next: any[]) => c.setAccounts(next.map(c.budgetLineToAccount)),
    save: async (next: any[]) => { if (!await c.saveBudget(next.map(c.budgetLineToAccount))) throw new Error('pageControl.failed') },
  }))
}

const askAiAdvisorThroughPageControl = async ({ question, options, fallback, translate, beforeAsk }: any) => {
  const control = (globalThis as any).PlummeAppControlSession
  if (!control || control.getSnapshot?.().busy) return fallback(question, options)
  // Remove the launching dialog before the session observes ordinary page controls.
  beforeAsk?.()
  const before = control.getSnapshot().messages.length
  await control.ask(question)
  const snapshot = control.getSnapshot()
  if (snapshot.error === 'control.agentUnavailable') return fallback(question, options)
  if (snapshot.error) return { answer: translate(snapshot.error, snapshot.error), lines: [], metadata: { intentName: 'assistant_agent', sourceType: 'agent' } }
  const replies = snapshot.messages.slice(before).filter((message: any) => message.role === 'assistant')
  if (!replies.length) return fallback(question, options)
  return { answer: replies.map((message: any) => message.content).filter(Boolean).join('\n'), lines: [], metadata: { intentName: 'assistant_agent', sourceType: 'agent' } }
}

Object.assign(globalThis, {
  createAiAdvisorPageControlAdapter,
  createBudgetPageControlAdapter,
  useAiAdvisorPageControlAdapter,
  useBudgetPageControlAdapter,
  askAiAdvisorThroughPageControl,
})
